import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import WorkoutProgressIndicator from '../../components/ui/WorkoutProgressIndicator';
import QuickActionButton from '../../components/ui/QuickActionButton';
import ExerciseCard from './components/ExerciseCard';
import SetLogger from './components/SetLogger';
import RestTimer from './components/RestTimer';
import ExerciseInstructions from './components/ExerciseInstructions';
import WorkoutSummary from './components/WorkoutSummary';
import WorkoutControls from './components/WorkoutControls';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ActiveWorkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentTenant } = useAuth();
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [currentSet, setCurrentSet] = useState(1);
  const [completedSets, setCompletedSets] = useState([]);
  const [showRestTimer, setShowRestTimer] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [workoutDuration, setWorkoutDuration] = useState(0);
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [workoutSessionId, setWorkoutSessionId] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [saving, setSaving] = useState(false);

  const workoutPlanId = location?.state?.planId;
  const workoutName = location?.state?.workoutName || 'Custom Workout';

  const workoutData = {
    name: "Upper Body Strength",
    exercises: [
    {
      id: 1,
      name: "Bench Press",
      sets: 4,
      reps: 10,
      weight: 135,
      targetMuscle: "Chest",
      difficulty: "Intermediate",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1585150e8-1767770922804.png",
      imageAlt: "Athletic man performing barbell bench press exercise on weight bench in modern gym with proper form",
      demonstrationImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1e153d13d-1766707659741.png",
      demonstrationImageAlt: "Detailed demonstration of proper bench press form showing starting position with barbell at chest level and arms extended",
      instructions: [
      "Lie flat on bench with feet firmly planted on floor",
      "Grip barbell slightly wider than shoulder width",
      "Lower bar to mid-chest with controlled motion",
      "Press bar up explosively while maintaining form",
      "Lock out arms at top without hyperextending elbows"],

      formTips: [
      "Keep shoulder blades retracted throughout movement",
      "Maintain natural arch in lower back",
      "Don\'t bounce bar off chest"],

      alternatives: [
      { name: "Dumbbell Bench Press", reason: "Easier on shoulders, better range of motion" },
      { name: "Push-ups", reason: "No equipment needed, bodyweight alternative" }]

    },
    {
      id: 2,
      name: "Barbell Rows",
      sets: 4,
      reps: 12,
      weight: 115,
      targetMuscle: "Back",
      difficulty: "Intermediate",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e3bf3193-1766860480503.png",
      imageAlt: "Muscular athlete performing bent-over barbell row exercise with proper hip hinge position in well-lit fitness center",
      demonstrationImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1e3bf3193-1766860480503.png",
      demonstrationImageAlt: "Step-by-step demonstration of barbell row showing proper bent-over position with neutral spine and barbell pulled to lower chest",
      instructions: [
      "Stand with feet hip-width apart, knees slightly bent",
      "Hinge at hips maintaining neutral spine",
      "Grip barbell with hands shoulder-width apart",
      "Pull barbell to lower chest, squeezing shoulder blades",
      "Lower with control to starting position"],

      formTips: [
      "Keep core engaged throughout movement",
      "Avoid using momentum to lift weight",
      "Lead with elbows, not hands"],

      alternatives: [
      { name: "Dumbbell Rows", reason: "Unilateral work, address imbalances" },
      { name: "Cable Rows", reason: "Constant tension, easier on lower back" }]

    },
    {
      id: 3,
      name: "Overhead Press",
      sets: 3,
      reps: 10,
      weight: 95,
      targetMuscle: "Shoulders",
      difficulty: "Intermediate",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ce4c0b67-1767133171270.png",
      imageAlt: "Fit woman performing standing overhead barbell press with arms fully extended above head in modern gym setting",
      demonstrationImage: "https://img.rocket.new/generatedImages/rocket_gen_img_13de5b10b-1766953676595.png",
      demonstrationImageAlt: "Complete overhead press movement demonstration showing starting position at shoulder height and full lockout overhead with proper posture",
      instructions: [
      "Stand with feet shoulder-width apart",
      "Hold barbell at shoulder height, elbows slightly forward",
      "Press bar overhead in straight line",
      "Lock out arms at top without leaning back",
      "Lower bar with control to shoulders"],

      formTips: [
      "Keep core tight to protect lower back",
      "Don\'t hyperextend spine at top",
      "Bar path should be straight up and down"],

      alternatives: [
      { name: "Dumbbell Press", reason: "Greater range of motion, shoulder-friendly" },
      { name: "Arnold Press", reason: "Targets all three deltoid heads" }]

    },
    {
      id: 4,
      name: "Bicep Curls",
      sets: 3,
      reps: 12,
      weight: 30,
      targetMuscle: "Biceps",
      difficulty: "Beginner",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_10b163d59-1765598781878.png",
      imageAlt: "Athletic man performing standing dumbbell bicep curls with proper form showing peak contraction in bright gym environment",
      demonstrationImage: "https://img.rocket.new/generatedImages/rocket_gen_img_11cf8629c-1766953671029.png",
      demonstrationImageAlt: "Detailed bicep curl form demonstration showing starting position with arms extended and ending position with dumbbells at shoulder level",
      instructions: [
      "Stand with feet hip-width apart, dumbbells at sides",
      "Keep elbows close to torso throughout movement",
      "Curl weights up while keeping upper arms stationary",
      "Squeeze biceps at top of movement",
      "Lower weights with control to starting position"],

      formTips: [
      "Don\'t swing or use momentum",
      "Keep wrists neutral, don't bend them",
      "Control the negative portion"],

      alternatives: [
      { name: "Hammer Curls", reason: "Targets brachialis, forearm development" },
      { name: "Cable Curls", reason: "Constant tension throughout range" }]

    },
    {
      id: 5,
      name: "Tricep Dips",
      sets: 3,
      reps: 15,
      weight: 0,
      targetMuscle: "Triceps",
      difficulty: "Intermediate",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a51fcf19-1767803719490.png",
      imageAlt: "Strong athlete performing parallel bar tricep dips with full range of motion in professional gym facility",
      demonstrationImage: "https://img.rocket.new/generatedImages/rocket_gen_img_19110b6a8-1769198959291.png",
      demonstrationImageAlt: "Complete tricep dip movement showing starting position with arms locked and bottom position with elbows at ninety degrees",
      instructions: [
      "Grip parallel bars with arms fully extended",
      "Lower body by bending elbows to 90 degrees",
      "Keep elbows close to body, don't flare out",
      "Press back up to starting position",
      "Maintain upright torso throughout"],

      formTips: [
      "Don't go too deep if shoulders hurt",
      "Keep shoulders down and back",
      "Engage core for stability"],

      alternatives: [
      { name: "Bench Dips", reason: "Easier variation, adjustable difficulty" },
      { name: "Close-Grip Push-ups", reason: "No equipment needed" }]

    },
    {
      id: 6,
      name: "Lateral Raises",
      sets: 3,
      reps: 15,
      weight: 20,
      targetMuscle: "Shoulders",
      difficulty: "Beginner",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_140e4a62a-1764998939658.png",
      imageAlt: "Fitness enthusiast performing standing dumbbell lateral raises with arms extended to shoulder height in spacious gym",
      demonstrationImage: "https://img.rocket.new/generatedImages/rocket_gen_img_140e4a62a-1764998939658.png",
      demonstrationImageAlt: "Lateral raise exercise demonstration showing starting position with dumbbells at sides and raised position with arms parallel to floor",
      instructions: [
      "Stand with feet shoulder-width apart, dumbbells at sides",
      "Raise arms out to sides until parallel with floor",
      "Keep slight bend in elbows throughout",
      "Pause briefly at top of movement",
      "Lower weights with control"],

      formTips: [
      "Don\'t use momentum or swing weights",
      "Lead with elbows, not hands",
      "Keep torso stable and upright"],

      alternatives: [
      { name: "Cable Lateral Raises", reason: "Constant tension, smoother movement" },
      { name: "Machine Lateral Raises", reason: "Easier to maintain form" }]

    },
    {
      id: 7,
      name: "Face Pulls",
      sets: 3,
      reps: 15,
      weight: 50,
      targetMuscle: "Rear Delts",
      difficulty: "Beginner",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_10eaa043c-1764694022014.png",
      imageAlt: "Athletic person performing cable face pulls exercise with rope attachment at face level in modern fitness studio",
      demonstrationImage: "https://img.rocket.new/generatedImages/rocket_gen_img_10eaa043c-1764694022014.png",
      demonstrationImageAlt: "Face pull exercise demonstration showing proper rope grip and pulling motion toward face with elbows high and wide",
      instructions: [
      "Set cable at upper chest height with rope attachment",
      "Grip rope with thumbs toward you",
      "Pull rope toward face, separating hands",
      "Squeeze shoulder blades together at end",
      "Return to start with control"],

      formTips: [
      "Keep elbows high throughout movement",
      "Focus on rear delts, not arms",
      "Don\'t lean back excessively"],

      alternatives: [
      { name: "Band Face Pulls", reason: "Portable, can do anywhere" },
      { name: "Reverse Flyes", reason: "Similar muscle activation" }]

    },
    {
      id: 8,
      name: "Plank Hold",
      sets: 3,
      reps: 60,
      weight: 0,
      targetMuscle: "Core",
      difficulty: "Beginner",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d013843f-1766836301641.png",
      imageAlt: "Determined athlete holding perfect plank position with straight body alignment on exercise mat in bright gym space",
      demonstrationImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1b31a81d7-1764695066555.png",
      demonstrationImageAlt: "Proper plank form demonstration showing forearm position, straight body line from head to heels, and engaged core muscles",
      instructions: [
      "Start in forearm plank position",
      "Keep body in straight line from head to heels",
      "Engage core and squeeze glutes",
      "Hold position for prescribed time",
      "Breathe steadily throughout"],

      formTips: [
      "Don\'t let hips sag or pike up",
      "Keep neck neutral, look at floor",
      "Distribute weight evenly on forearms"],

      alternatives: [
      { name: "Knee Plank", reason: "Easier variation for beginners" },
      { name: "Side Plank", reason: "Targets obliques more" }]

    }]

  };

  const currentExercise = workoutData?.exercises?.[currentExerciseIndex];
  const totalCompletedSets = completedSets?.length;
  const totalSets = workoutData?.exercises?.reduce((sum, ex) => sum + ex?.sets, 0);

  useEffect(() => {
    if (user?.id && currentTenant?.id) {
      initializeWorkoutSession();
    }
  }, [user, currentTenant]);

  useEffect(() => {
    let interval;
    if (!isPaused && !showFinishModal && startTime) {
      interval = setInterval(() => {
        setWorkoutDuration(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPaused, showFinishModal, startTime]);

  const initializeWorkoutSession = async () => {
    try {
      const now = new Date()?.toISOString();
      setStartTime(Date.now());

      const totalSets = workoutData?.exercises?.reduce((sum, ex) => sum + ex?.sets, 0);

      const { data, error } = await supabase?.
      from('workout_completion_logs')?.
      insert({
        tenant_id: currentTenant?.id,
        user_id: user?.id,
        workout_plan_id: workoutPlanId,
        workout_name: workoutData?.name,
        total_exercises: workoutData?.exercises?.length,
        completed_exercises: 0,
        total_sets: totalSets,
        completed_sets: 0,
        started_at: now
      })?.
      select()?.
      single();

      if (error) throw error;
      setWorkoutSessionId(data?.id);
    } catch (err) {
      console.error('Error initializing workout session:', err);
    }
  };

  const handleLogSet = async (setData) => {
    try {
      setSaving(true);
      const currentExercise = workoutData?.exercises?.[currentExerciseIndex];

      await supabase?.
      from('workout_logs')?.
      insert({
        tenant_id: currentTenant?.id,
        user_id: user?.id,
        workout_session_id: workoutSessionId,
        exercise_name: currentExercise?.name,
        set_number: setData?.setNumber,
        reps: setData?.reps,
        weight: setData?.weight,
        completed_at: new Date()?.toISOString()
      });

      const newCompletedSets = [...completedSets, { exerciseIndex: currentExerciseIndex, setNumber: currentSet, ...setData }];
      setCompletedSets(newCompletedSets);

      await supabase?.
      from('workout_completion_logs')?.
      update({ completed_sets: newCompletedSets?.length })?.
      eq('id', workoutSessionId);

      if (currentSet < currentExercise?.sets) {
        setCurrentSet(currentSet + 1);
        setShowRestTimer(true);
      } else {
        handleNextExercise();
      }
    } catch (err) {
      console.error('Error logging set:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSkipSet = () => {
    const currentExercise = workoutData?.exercises?.[currentExerciseIndex];
    if (currentSet < currentExercise?.sets) {
      setCurrentSet(currentSet + 1);
    } else {
      handleNextExercise();
    }
  };

  const handleRestComplete = () => {
    setShowRestTimer(false);
  };

  const handleSkipRest = () => {
    setShowRestTimer(false);
  };

  const handlePreviousExercise = () => {
    if (currentExerciseIndex > 0) {
      setCurrentExerciseIndex((prev) => prev - 1);
      setCurrentSet(1);
    }
  };

  const handleNextExercise = async () => {
    const completedExercisesCount = currentExerciseIndex + 1;

    await supabase?.
    from('workout_completion_logs')?.
    update({ completed_exercises: completedExercisesCount })?.
    eq('id', workoutSessionId);

    if (currentExerciseIndex < workoutData?.exercises?.length - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
      setCurrentSet(1);
      setShowRestTimer(false);
    } else {
      setShowFinishModal(true);
    }
  };

  const handlePauseWorkout = () => {
    setIsPaused(true);
  };

  const handleResumeWorkout = () => {
    setIsPaused(false);
  };

  const handleFinishWorkout = async () => {
    try {
      const estimatedCalories = Math.floor(workoutDuration / 60) * 5;

      await supabase?.
      from('workout_completion_logs')?.
      update({
        completed_at: new Date()?.toISOString(),
        duration_minutes: Math.floor(workoutDuration / 60),
        calories_burned: estimatedCalories
      })?.
      eq('id', workoutSessionId);

      navigate('/workout-analytics-dashboard');
    } catch (err) {
      console.error('Error finishing workout:', err);
    }
  };

  const handleEmergencyStop = () => {
    navigate('/workout-library');
  };

  const handleConfirmFinish = () => {
    navigate('/member-dashboard');
  };

  const estimatedCalories = Math.round(workoutDuration / 60 * 8);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-[1440px] px-4 md:px-6 lg:px-8 py-6 lg:py-8 pb-24 lg:pb-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/workout-library')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-smooth mb-4">

            <Icon name="ArrowLeft" size={20} />
            <span className="text-sm font-medium">Back to Workout Library</span>
          </button>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-2">
                {workoutData?.name}
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Stay focused and maintain proper form throughout each exercise
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-primary/10 rounded-lg">
                <p className="text-xs caption text-muted-foreground">Duration</p>
                <p className="text-lg font-bold data-text text-primary">
                  {Math.floor(workoutDuration / 60)}:{(workoutDuration % 60)?.toString()?.padStart(2, '0')}
                </p>
              </div>
              <div className="px-4 py-2 bg-accent/10 rounded-lg">
                <p className="text-xs caption text-muted-foreground">Calories</p>
                <p className="text-lg font-bold data-text text-accent">
                  {estimatedCalories}
                </p>
              </div>
            </div>
          </div>
        </div>

        <WorkoutProgressIndicator
          currentExercise={currentExerciseIndex + 1}
          totalExercises={workoutData?.exercises?.length}
          exerciseName={currentExercise?.name}
          onPrevious={handlePreviousExercise}
          onNext={handleNextExercise}
          onPause={handlePauseWorkout} />


        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2 space-y-6">
            <ExerciseInstructions exercise={currentExercise} />

            <SetLogger
              currentSet={currentSet}
              totalSets={currentExercise?.sets}
              onLogSet={handleLogSet}
              onSkipSet={handleSkipSet}
              previousWeight={currentExercise?.weight}
              previousReps={currentExercise?.reps} />


            <WorkoutControls
              onPause={handlePauseWorkout}
              onResume={handleResumeWorkout}
              onFinish={handleFinishWorkout}
              onEmergencyStop={handleEmergencyStop}
              isPaused={isPaused} />

          </div>

          <div className="space-y-6">
            <WorkoutSummary
              totalExercises={workoutData?.exercises?.length}
              completedExercises={currentExerciseIndex}
              totalSets={totalSets}
              completedSets={totalCompletedSets}
              duration={workoutDuration}
              caloriesBurned={estimatedCalories} />


            <div className="bg-card border border-border rounded-lg shadow-md p-4 md:p-6">
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                Exercise List
              </h3>
              <div className="space-y-2 max-h-[600px] overflow-y-auto overflow-elegant">
                {workoutData?.exercises?.map((exercise, index) =>
                <ExerciseCard
                  key={exercise?.id}
                  exercise={exercise}
                  isActive={index === currentExerciseIndex}
                  isCompleted={index < currentExerciseIndex}
                  onClick={() => {
                    setCurrentExerciseIndex(index);
                    setCurrentSet(1);
                  }} />

                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <RestTimer
        duration={90}
        onComplete={handleRestComplete}
        onSkip={handleSkipRest}
        isActive={showRestTimer} />

      {showFinishModal &&
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg p-6 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={32} className="text-success" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                Workout Complete!
              </h2>
              <p className="text-muted-foreground">
                Great job! You completed {completedSets?.length} sets across {completedExercisesCount} exercises.
              </p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between p-3 bg-muted rounded-lg">
                <span className="text-muted-foreground">Duration</span>
                <span className="font-semibold text-foreground">
                  {Math.floor(workoutDuration / 60)} min
                </span>
              </div>
              <div className="flex justify-between p-3 bg-muted rounded-lg">
                <span className="text-muted-foreground">Calories Burned</span>
                <span className="font-semibold text-foreground">
                  {Math.floor(workoutDuration / 60) * 5} cal
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
              variant="outline"
              onClick={() => navigate('/member-dashboard')}
              className="flex-1">

                Go to Dashboard
              </Button>
              <Button
              onClick={handleFinishWorkout}
              className="flex-1"
              iconName="BarChart3"
              iconPosition="left">

                View Analytics
              </Button>
            </div>
          </div>
        </div>
      }
      {isPaused &&
      <div className="fixed inset-0 z-[105] bg-background/80 backdrop-blur-sm flex items-center justify-center">
          <div className="text-center">
            <Icon name="Pause" size={64} color="var(--color-warning)" strokeWidth={2} />
            <p className="text-2xl font-heading font-bold text-foreground mt-4">
              Workout Paused
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Take your time, resume when ready
            </p>
          </div>
        </div>
      }
      <MobileBottomNav />
      <QuickActionButton />
    </div>);

};

export default ActiveWorkout;